import PhoneInputWithCountry from 'react-phone-number-input'
import type { Country } from 'react-phone-number-input'
import './_styledPhoneInput.scss'

interface StyledPhoneInputProps {
  phone: string
  setPhone: (value: string) => void
  maskedPhone?: string   // e.g. "******7776"
  countryName?: string   // e.g. "Canada", "Japan"
  customClass?: string
  onFocus?: () => void
}

// Map backend country_name -> ISO 3166-1 alpha-2 code for react-phone-number-input
const COUNTRY_NAME_TO_CODE: Record<string, Country> = {
  // North America
  'canada': 'CA',
  'united states': 'US',
  'united states of america': 'US',
  'usa': 'US',
  'u.s.a.': 'US',
  'mexico': 'MX',

  // UK & Ireland
  'united kingdom': 'GB',
  'uk': 'GB',
  'great britain': 'GB',
  'england': 'GB',
  'scotland': 'GB',
  'wales': 'GB',
  'northern ireland': 'GB',
  'ireland': 'IE',

  // Western Europe
  'france': 'FR',
  'germany': 'DE',
  'spain': 'ES',
  'portugal': 'PT',
  'italy': 'IT',
  'belgium': 'BE',
  'netherlands': 'NL',
  'luxembourg': 'LU',
  'switzerland': 'CH',
  'austria': 'AT',
  'monaco': 'MC',
  'andorra': 'AD',
  'liechtenstein': 'LI',

  // Nordics
  'denmark': 'DK',
  'sweden': 'SE',
  'norway': 'NO',
  'finland': 'FI',
  'iceland': 'IS',

  // Eastern Europe & Balkans
  'poland': 'PL',
  'czech republic': 'CZ',
  'czechia': 'CZ',
  'slovakia': 'SK',
  'hungary': 'HU',
  'romania': 'RO',
  'bulgaria': 'BG',
  'slovenia': 'SI',
  'croatia': 'HR',
  'serbia': 'RS',
  'bosnia and herzegovina': 'BA',
  'bosnia & herzegovina': 'BA',
  'montenegro': 'ME',
  'north macedonia': 'MK',
  'albania': 'AL',
  'greece': 'GR',

  // Baltics
  'estonia': 'EE',
  'latvia': 'LV',
  'lithuania': 'LT',

  // CIS / Former USSR
  'russia': 'RU',
  'ukraine': 'UA',
  'belarus': 'BY',
  'moldova': 'MD',
  'georgia': 'GE',
  'armenia': 'AM',
  'azerbaijan': 'AZ',
  'kazakhstan': 'KZ',
  'uzbekistan': 'UZ',
  'turkmenistan': 'TM',
  'kyrgyzstan': 'KG',
  'tajikistan': 'TJ',

  // Middle East
  'turkey': 'TR',
  'cyprus': 'CY',
  'israel': 'IL',
  'palestine': 'PS',
  'jordan': 'JO',
  'lebanon': 'LB',
  'syria': 'SY',
  'iraq': 'IQ',
  'iran': 'IR',
  'saudi arabia': 'SA',
  'united arab emirates': 'AE',
  'uae': 'AE',
  'qatar': 'QA',
  'kuwait': 'KW',
  'bahrain': 'BH',
  'oman': 'OM',
  'yemen': 'YE',

  // Africa (common)
  'egypt': 'EG',
  'morocco': 'MA',
  'tunisia': 'TN',
  'algeria': 'DZ',
  'libya': 'LY',
  'south africa': 'ZA',
  'nigeria': 'NG',
  'ghana': 'GH',
  'kenya': 'KE',
  'ethiopia': 'ET',
  'uganda': 'UG',
  'tanzania': 'TZ',
  'rwanda': 'RW',
  'senegal': 'SN',
  'ivory coast': 'CI',
  "côte d'ivoire": 'CI',
  'cameroon': 'CM',
  'angola': 'AO',
  'mozambique': 'MZ',
  'zambia': 'ZM',
  'zimbabwe': 'ZW',
  'namibia': 'NA',
  'botswana': 'BW',
  'mauritius': 'MU',
  'madagascar': 'MG',

  // Asia
  'china': 'CN',
  'hong kong': 'HK',
  'macau': 'MO',
  'taiwan': 'TW',
  'japan': 'JP',
  'south korea': 'KR',
  'korea (south)': 'KR',
  'north korea': 'KP',
  'korea (north)': 'KP',
  'india': 'IN',
  'pakistan': 'PK',
  'bangladesh': 'BD',
  'sri lanka': 'LK',
  'nepal': 'NP',
  'bhutan': 'BT',
  'maldives': 'MV',
  'thailand': 'TH',
  'vietnam': 'VN',
  'laos': 'LA',
  'cambodia': 'KH',
  'myanmar': 'MM',
  'indonesia': 'ID',
  'malaysia': 'MY',
  'singapore': 'SG',
  'philippines': 'PH',
  'brunei': 'BN',

  // Oceania
  'australia': 'AU',
  'new zealand': 'NZ',
  'fiji': 'FJ',
  'papua new guinea': 'PG',
  'samoa': 'WS',
  'tonga': 'TO',
  'vanuatu': 'VU',

  // Central & South America
  'guatemala': 'GT',
  'belize': 'BZ',
  'honduras': 'HN',
  'el salvador': 'SV',
  'nicaragua': 'NI',
  'costa rica': 'CR',
  'panama': 'PA',
  'colombia': 'CO',
  'venezuela': 'VE',
  'ecuador': 'EC',
  'peru': 'PE',
  'bolivia': 'BO',
  'paraguay': 'PY',
  'chile': 'CL',
  'argentina': 'AR',
  'uruguay': 'UY',
  'guyana': 'GY',
  'suriname': 'SR',

  // Caribbean
  'cuba': 'CU',
  'haiti': 'HT',
  'dominican republic': 'DO',
  'jamaica': 'JM',
  'trinidad and tobago': 'TT',
  'bahamas': 'BS',
  'barbados': 'BB',
  'saint lucia': 'LC',
  'grenada': 'GD',
  'saint kitts and nevis': 'KN',
  'saint vincent and the grenadines': 'VC',

  // Microstates & misc
  'san marino': 'SM',
  'vatican city': 'VA',
  'seychelles': 'SC',
  'cape verde': 'CV'
}

function mapCountryNameToCode(name?: string): Country | undefined {
  if (!name) return undefined
  const normalized = name.trim().toLowerCase()
  return COUNTRY_NAME_TO_CODE[normalized]
}

function StyledPhoneInput({
  phone,
  setPhone,
  maskedPhone,
  countryName,
  customClass = '',
  onFocus
}: StyledPhoneInputProps) {
  const defaultCountry = mapCountryNameToCode(countryName)

  return (
    <PhoneInputWithCountry
      className={customClass}
      value={phone || undefined}                         // real E.164 or empty
      onChange={(val?: string) => setPhone(val ?? '')}
      placeholder={maskedPhone ?? 'Enter phone number'}  // "******7776" appears here
      limitMaxLength
      smartCaret={false}
      onFocus={onFocus}
      defaultCountry={defaultCountry}                   // initial flag from profile
      international                                     // treat leading digits as country code
      countryCallingCodeEditable                        // allow editing the calling code
    />
  )
}

export default StyledPhoneInput
